/**
 * What the visitor filled in the quotation form, kept while they sign in, register or activate
 * the account PhoneTec created for them at the counter (ADR 0008: the estimate needs an
 * account).
 *
 * The answers are kept in the browser's storage, so they survive a reload and the activation
 * link, which opens in another tab. The photos are `File` objects and cannot be stored there:
 * they are kept in memory, which covers moving between pages, and a draft restored without them
 * says so. A draft is forgotten after a day -- the life of the activation link -- or once the
 * request is sent.
 */
export type QuotationDraftFields = {
  step: number;
  selectedBrand: string;
  selectedModelId: string;
  storageGb: string;
  answers: Record<string, string>;
  declaredCondition: string;
  /// How many photos had been chosen, so a draft that lost them can say so.
  photoCount: number;
};

export type QuotationDraft = QuotationDraftFields & {
  photos: File[];
};

const STORAGE_KEY = 'phonetec.quotationDraft';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/// In memory only: survives moving between pages, not a reload.
let photosInMemory: File[] = [];

/// Storage can be missing or refuse writes (private windows, blocked site data).
function readStored(): { savedAt: number; fields: QuotationDraftFields } | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as { savedAt: number; fields: QuotationDraftFields }) : null;
  } catch {
    return null;
  }
}

export function saveQuotationDraft(draft: QuotationDraft): void {
  const { photos, ...fields } = draft;
  photosInMemory = photos;

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ savedAt: Date.now(), fields: { ...fields, photoCount: photos.length } }),
    );
  } catch {
    // Without storage the draft lives only in memory: still enough to move between pages.
  }
}

/** The draft, if one younger than a day exists. The photos only if this tab still holds them. */
export function loadQuotationDraft(): QuotationDraft | null {
  const stored = readStored();

  if (!stored || Date.now() - stored.savedAt > MAX_AGE_MS) {
    clearQuotationDraft();
    return null;
  }

  return { ...stored.fields, photos: photosInMemory };
}

export function hasQuotationDraft(): boolean {
  return loadQuotationDraft() !== null;
}

export function clearQuotationDraft(): void {
  photosInMemory = [];

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored, nothing to remove.
  }
}
